import { useEffect } from "react";
import { isEqual } from "lodash";
import { onFeatureUsage } from "@/utils/gtm";
import useTabStore from "@/stores/useTabStore";
import { useDemoGuideStore } from "./useDemoGuideStore";
import { visibleSteps } from "./visibleSteps";
import { DEMO_WATCHLIST_ID } from "@/pages/PortfolioBuilder/demoWatchlist";
import { matches } from "@/onboarding/rail/triggers";
import type { WidgetFilterData } from "@/utils/DashboardLayouts";

/**
 * Did this widget's filter/highlight selection just come to include the demo
 * watchlist?
 *
 * The highlight step asks them to apply the list they built, so "any
 * widgetFilters write" is too loose a test — opening a panel, clearing a
 * selection or any incidental write satisfied it, which is how the run could
 * reach the popout step without the watchlist ever being applied.
 */
function selectsDemoWatchlist(data: WidgetFilterData | undefined): boolean {
  if (!data) return false;
  const id = String(DEMO_WATCHLIST_ID);
  const sections = [
    ...Object.values(data.filter ?? {}),
    ...Object.values(data.highlight ?? {}),
  ];
  return sections.some((values) => values?.includes(id));
}

/**
 * Advances the guided run by watching the product's existing instrumentation.
 *
 * Same approach as src/onboarding/checklist/useChecklistDetection.ts, and for
 * the same reason: the alternative is threading "tell the guide I happened"
 * callbacks into every widget the guide might point at. Subscribing to
 * `onFeatureUsage` instead means the guide observes the product without the
 * product knowing it exists — and the signals are already emitted for GA4, so
 * a step that can be measured is a step that can be detected.
 */
// `matches` now lives in src/onboarding/rail/triggers.ts, shared with the
// subscriber rail. Imported above.

export function useStepDetection(enabled: boolean) {
  const advanceStep = useDemoGuideStore((s) => s.advanceStep);
  const markPoppedOut = useDemoGuideStore((s) => s.markPoppedOut);

  useEffect(() => {
    if (!enabled) return;

    /** Resolve the live step at fire time — never close over stale state. */
    const currentStep = () => {
      const state = useDemoGuideStore.getState().state;
      if (!state?.assetClass || state.completedAt || state.exitedAt) return null;
      return visibleSteps(state.assetClass)[state.step] ?? null;
    };

    const unsubscribeFeatureUsage = onFeatureUsage((event) => {
      // Popping out is recorded whether or not it was the step being asked
      // for — it is a checklist entry in its own right so it stays reachable
      // from any path, including before the run starts.
      if (event.feature_name === "widget" && event.feature_action === "popout") {
        markPoppedOut();
      }

      const step = currentStep();
      if (step && matches(step.trigger, event)) advanceStep();
    });

    // Any widgetFilters change on any tab = the user drove a filter or
    // highlight panel. Covers every widget's panel without touching them.
    //
    // Deliberately WITHOUT useChecklistDetection's `if (!state.tabsLoaded)`
    // guard. That flag only goes true after a successful authenticated read of
    // the server layout, so for a logged-out visitor it is false forever and
    // the guard would silently disable this whole subscription. It exists
    // there to avoid acting on the pre-load default layout; here the default
    // layout IS the layout.
    const unsubscribeTabStore = useTabStore.subscribe((state, prevState) => {
      const step = currentStep();
      if (!step || step.trigger.kind !== "widgetFilters") return;

      const applied = state.tabs.some((tab) => {
        const prevTab = prevState.tabs.find((t) => t.id === tab.id);
        if (!prevTab || isEqual(tab.widgetFilters, prevTab.widgetFilters)) {
          return false;
        }
        // Only the demo watchlist counts, and only when it was not already
        // selected — otherwise unrelated edits to the same widget re-fire it.
        return Object.entries(tab.widgetFilters ?? {}).some(
          ([widgetId, data]) =>
            selectsDemoWatchlist(data) &&
            !selectsDemoWatchlist(prevTab.widgetFilters?.[widgetId])
        );
      });
      if (applied) advanceStep();
    });

    return () => {
      unsubscribeFeatureUsage();
      unsubscribeTabStore();
    };
  }, [enabled, advanceStep, markPoppedOut]);
}
